/**
 * The web checkout's conversation, as two decisions: why a purchase was refused, and what the tab
 * does with a trainer who has just come back from the payment page.
 *
 * ── Coming back grants nothing ──────────────────────────────────────────────────────────────
 * Subscription requirement R1: the server alone decides what an account may do, and it decides it
 * from the channel's own event. The seconds between paying and being entitled are an ACTIVATING
 * state, never access — so the return address is not evidence of anything, it is only the trigger
 * for one call to the backend, and the backend's outcome is what this machine turns.
 *
 * ── The session id survives a reload ────────────────────────────────────────────────────────
 * The address is cleared as soon as it has been read (a return URL is not a link to share, and a
 * reload of one would re-run the completion), so the id is remembered for the length of the wait
 * and dropped the moment the wait ends. Where it is remembered is `lib/useWebCheckout`'s; whether
 * it should be is here.
 *
 * Pure: `t` arrives as a parameter, and the elapsed milliseconds arrive as a number.
 */

import type { TFunction } from 'i18next';

import {
  TrainWithMeModulesSubscriptionsProcessorsWebCheckoutOutcome as Outcome,
  type TrainWithMeModulesSubscriptionsProcessorsWebCheckoutOutcome as OutcomeValue,
} from '~/shared/api/generated/model';

import { channelName } from './subscriptionCard';

/* ── Why a purchase was refused ─────────────────────────────────────────────────────────────── */

/**
 * As much of the backend's Problem Details as this decision reads.
 *
 * The two extension members are RFC 9457 extensions rather than fields of the base document, and
 * they are optional here for the same reason the code is: a proxy's 502, a timeout and a body that
 * is not a problem document at all all arrive as "no code", and every one of them is the generic
 * failure rather than a crash.
 */
export interface CheckoutProblem {
  readonly code?: string | undefined;
  /** `store_subscription_live` carries the channel the live subscription was bought on. */
  readonly channel?: string | undefined;
  /** `comp_level_not_exceeded` carries the level the admin granted. */
  readonly compPlanCode?: string | undefined;
}

/**
 * The catalogue's own name for a plan code, or `null` where the catalogue cannot name it.
 *
 * The refusal is the one place a plan CODE could reach the trainer, and a code is an internal name:
 * „Masz przyznany plan pro." is the backend's vocabulary leaking onto the screen, and an extension
 * that did not survive a proxy made it „Masz przyznany plan ." Whoever holds the catalogue resolves
 * it; this module only decides which sentence the answer picks.
 */
export type PlanNameLookup = (code: string) => string | null;

/**
 * The sentence a refused checkout puts under the cards.
 *
 * Every arm says where the subscription IS managed, because that is the only useful thing left to
 * say: requirement R4 gives the selling channel exclusive control, so the tab's job here is to
 * point at it rather than to offer a control it does not have.
 */
export function checkoutRefusalMessage(
  problem: CheckoutProblem,
  t: TFunction,
  planNameOf: PlanNameLookup,
): string {
  switch (problem.code) {
    case 'store_subscription_live': {
      const store = problem.channel === undefined ? null : channelName(problem.channel, t);
      return store === null
        ? t('web:account.subscription.refusal.storeLiveElsewhere')
        : t('web:account.subscription.refusal.storeLive', { store });
    }
    case 'web_subscription_live':
      return t('web:account.subscription.refusal.webLive');
    case 'comp_level_not_exceeded': {
      // A name or no name at all. There is no third sentence here with a code in it.
      const plan = problem.compPlanCode === undefined ? null : planNameOf(problem.compPlanCode);
      return plan === null
        ? t('web:account.subscription.refusal.compNotExceededPlain')
        : t('web:account.subscription.refusal.compNotExceeded', { plan });
    }
    default:
      return t('web:account.subscription.refusal.failed');
  }
}

/**
 * Where a refusal is said: under the trial-code field, or in the banner under the cards.
 *
 * · `code` — the CODE was refused (R19's three answers). The card goes back to the field with the
 *   code in it and the message under it, because the code is the thing the trainer can change.
 * · `banner` — the ACCOUNT or the provider was. R11's live-subscription answers and every failure
 *   with no code keep their banner, and a code on the card stays applied: it was never the problem.
 */
export interface CheckoutRefusal {
  readonly target: 'code' | 'banner';
  readonly message: string;
}

const TRIAL_CODE_REFUSALS: ReadonlyMap<string, string> = new Map([
  ['trial_code_invalid', 'web:account.subscription.trial.refusal.invalid'],
  ['trial_code_limit_reached', 'web:account.subscription.trial.refusal.limitReached'],
  ['trial_code_already_used', 'web:account.subscription.trial.refusal.alreadyUsed'],
]);

export function checkoutRefusal(
  problem: CheckoutProblem,
  t: TFunction,
  planNameOf: PlanNameLookup,
): CheckoutRefusal {
  const codeKey = problem.code === undefined ? undefined : TRIAL_CODE_REFUSALS.get(problem.code);
  return codeKey === undefined
    ? { target: 'banner', message: checkoutRefusalMessage(problem, t, planNameOf) }
    : { target: 'code', message: t(codeKey) };
}

/* ── Coming back from the payment page ──────────────────────────────────────────────────────── */

/**
 * Every 5 seconds, for a minute — the owner's window for an ingest that normally takes two.
 *
 * FIVE, not three. The return endpoint shares the account's rate limit, which allows 12 calls a
 * minute; at three seconds the wait asks for about twenty and the last third of the minute is spent
 * being refused, so the cap the trainer is shown is longer than the wait that actually runs. Twelve
 * calls over sixty seconds is the budget, and five seconds fits inside it with room for the retry
 * press that re-arms the same minute.
 */
export const RETURN_POLL_INTERVAL_MS = 5_000;
export const RETURN_POLL_LIMIT_MS = 60_000;

/**
 * · `idle` — the picker, unchanged. Nothing was bought, or nothing was being waited for.
 * · `completing` — the one call carrying the checkout session id is owed or in flight.
 * · `activating` — the server has the purchase and does not yet entitle it. Polling.
 * · `nothingPurchased` — the server looked and found no purchase at all. The picker, plus a line
 *   saying so: a trainer who came back from the payment page and is silently offered the same three
 *   cards cannot tell whether they were charged, which is the one question they have.
 * · `stillActivating` — the minute ran out. The tab says to come back rather than spinning for ever.
 * · `settled` — the entitlement reflects the purchase; the tab is the subscriber's tab now.
 */
export type CheckoutReturnPhase =
  | 'idle'
  | 'completing'
  | 'activating'
  | 'nothingPurchased'
  | 'stillActivating'
  | 'settled';

/** What the tab does on arrival, decided from the address and from what a reload left behind. */
export interface CheckoutArming {
  readonly phase: CheckoutReturnPhase;
  /** The id to send with the first call. `null` sends none, which is the self-healing sweep. */
  readonly sessionId: string | null;
  /** Whether the two checkout parameters have to be taken out of the address. */
  readonly clearAddress: boolean;
  /** What to keep for a reload: an id, or `null` to forget whatever is stored. */
  readonly remember: string | null;
}

export interface CheckoutArmingInput {
  /** `?checkout=` — `return` after a payment, `cancel` after backing out of one. */
  readonly checkout: string | null;
  /** `?session_id=` — the payment provider's own, written into the return URL by the backend. */
  readonly sessionId: string | null;
  /** What a previous visit stored before the browser was reloaded mid-wait. */
  readonly stored: string | null;
}

/**
 * What the tab owes on arrival.
 *
 * Three ways in, and the third is the one that is easy to forget: a trainer who reloaded the page
 * while the subscription was activating arrives with a clean address and an unfinished wait, and
 * the stored id is the only thing that says so.
 *
 * A cancelled checkout is not a failure and says nothing: the picker is already the right screen,
 * so the parameters come off the address and the stored id is dropped.
 */
export function armCheckoutReturn({
  checkout,
  sessionId,
  stored,
}: CheckoutArmingInput): CheckoutArming {
  if (checkout === 'return') {
    const id = sessionId ?? stored;
    return { phase: 'completing', sessionId: id, clearAddress: true, remember: id };
  }
  if (checkout === 'cancel') {
    return { phase: 'idle', sessionId: null, clearAddress: true, remember: null };
  }
  if (stored !== null && stored !== '') {
    return { phase: 'completing', sessionId: stored, clearAddress: false, remember: stored };
  }
  return { phase: 'idle', sessionId: null, clearAddress: false, remember: null };
}

/** What the outcome of one return call means. `failed` is a call that never got an answer. */
export type CheckoutReturnOutcome = OutcomeValue | 'failed';

export interface CheckoutReturnStep {
  readonly phase: CheckoutReturnPhase;
  /** Whether another call is owed after `RETURN_POLL_INTERVAL_MS`. */
  readonly poll: boolean;
  /** Whether the stored session id may be dropped — the wait is over, one way or another. */
  readonly forget: boolean;
}

export interface CheckoutReturnStepInput {
  readonly outcome: CheckoutReturnOutcome;
  /** How long the whole wait has been running. The cap is measured from the first call, not the last. */
  readonly waitedMs: number;
}

/**
 * The next state of the wait.
 *
 * A call that FAILED is treated as "not yet", not as an error: the provider being unreachable for
 * three seconds says nothing about whether the payment landed, and a red banner over a purchase
 * that is about to complete is the wrong answer. The minute's cap is what stops it either way.
 */
export function checkoutReturnStep({
  outcome,
  waitedMs,
}: CheckoutReturnStepInput): CheckoutReturnStep {
  if (outcome === Outcome.Entitled) {
    return { phase: 'settled', poll: false, forget: true };
  }
  if (outcome === Outcome.NothingPurchased) {
    // NOT `idle`: idle is „nothing was being waited for", and this is „something was, and it came to
    // nothing". Backing out of the payment page is the silent one — `?checkout=cancel` never gets
    // here, because a cancel arms no wait at all.
    return { phase: 'nothingPurchased', poll: false, forget: true };
  }
  if (waitedMs >= RETURN_POLL_LIMIT_MS) {
    return { phase: 'stillActivating', poll: false, forget: true };
  }
  return { phase: 'activating', poll: true, forget: false };
}

/* ── Leaving for the provider ───────────────────────────────────────────────────────────────── */

/**
 * Whether an address the backend handed back may replace this document.
 *
 * The answer to a checkout call is a URL this app then assigns to `window.location`, which is the
 * one place a string off the wire becomes navigation. `https:` only, so a `javascript:` or `data:`
 * URL in a compromised or mis-built answer is a refused checkout rather than script running in the
 * session's own origin — the session lives in `localStorage`, so that is account takeover.
 *
 * `http:` is allowed on loopback and nowhere else: the local stack and the end-to-end suite serve
 * the stubbed provider over plain HTTP, and a rule they cannot satisfy is a rule nobody runs.
 */
export function isLeavableCheckoutUrl(url: string | null | undefined): url is string {
  if (typeof url !== 'string' || url === '') return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Relative, or not a URL at all. The provider's page is neither.
    return false;
  }
  if (parsed.protocol === 'https:') return true;
  // `hostname` is the PARSED host, so `localhost.evil.com`, `127.0.0.1.evil` and a userinfo host
  // like `http://localhost@evil.example` are all somebody else's name and all refused here. IPv6
  // loopback is listed rather than reached by the two IPv4 spellings: a machine resolving
  // `localhost` to IPv6 serves the stubbed provider at an address the older rule refused. It is
  // spelled ONCE, with its brackets — `new URL('http://[::1]/').hostname` is `[::1]`, brackets and
  // all, so a bare `::1` comparison was a branch no address could ever reach.
  return (
    parsed.protocol === 'http:' &&
    (parsed.hostname === 'localhost' ||
      parsed.hostname === '127.0.0.1' ||
      parsed.hostname === '[::1]')
  );
}
